import { useMemo, useState } from "react";
import { Empty, Input, Modal, Segmented, Spin } from "antd";
import { useQuery } from "react-query";

import { LetterApi, OrganizationApi, ProjectApi } from "../../services";
import { useDebounce, useModules } from "../../hooks";
import ChatEntityCard from "../ChatEntityCard";
import ModalTitle from "../ModalTitle";
import styles from "./styles.module.css";

/* Each source turns its own list endpoint into the shape the API expects back
 * on send — a type and an id — plus what the card needs to draw itself. The
 * search is what makes this usable at all: an organization with a few hundred
 * tasks is not a dropdown.
 *
 * Only the types with a real destination in the panel are offered. The API
 * accepts sprints, docs and customers too, so adding them here later is a
 * source entry and a route, not a migration. */
const SOURCES = {
  task: {
    label: "تسک",
    module: null,
    /* `scope: org` widens the search past the caller's own tasks when they have
     * the permission for it; the API quietly falls back to "mine" when they do
     * not, so asking for it is safe. */
    fetch: (search) =>
      OrganizationApi.searchTasks({
        params: { search, scope: "org", per_page: 25 },
      }).then((res) =>
        (res.data.tasks || []).map((task) => ({
          type: "task",
          id: task.id,
          uuid: task.uuid,
          title: task.title,
          context: task.project?.title,
          project_uuid: task.project?.uuid,
          is_subtask: Boolean(task.parent_id || task.parent),
        })),
      ),
  },
  project: {
    label: "پروژه",
    module: null,
    fetch: (search) =>
      ProjectApi.getProjects({ params: { search, per_page: 25 } }).then((res) =>
        (res.data.projects || []).map((project) => ({
          type: "project",
          id: project.id,
          uuid: project.uuid,
          title: project.title,
          project_uuid: project.uuid,
        })),
      ),
  },
  letter: {
    label: "نامه",
    module: "letters",
    fetch: (search) =>
      LetterApi.getLetters({ params: { search, per_page: 25 } }).then((res) =>
        (res.data.letters || []).map((letter) => ({
          type: "letter",
          id: letter.id,
          uuid: letter.uuid,
          title: letter.subject,
          context: letter.sender_name || letter.recipient_name || undefined,
        })),
      ),
  },
};

/** Pick something in the panel to attach to a chat message. */
const ChatEntityPicker = ({ open, onClose, onPick }) => {
  const { has: hasModule } = useModules();
  const [type, setType] = useState("task");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useDebounce();

  const options = useMemo(
    () =>
      Object.entries(SOURCES)
        .filter(([, source]) => !source.module || hasModule(source.module))
        .map(([value, source]) => ({ value, label: source.label })),
    [hasModule],
  );

  /* The task endpoint rejects a single character, and a one-letter search over
   * a whole organization is not a useful list anyway. */
  const term = (search || "").trim();
  const enabled = open && term.length >= 2;

  const { data, isFetching } = useQuery({
    queryKey: ["[Search] ChatEntity", type, term],
    queryFn: () => SOURCES[type].fetch(term),
    enabled,
    initialData: [],
    keepPreviousData: true,
  });

  const reset = () => {
    setSearchInput("");
    setSearch("");
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={null}
      className="ds-modal"
      destroyOnClose
      title={
        <ModalTitle
          tone="blue"
          title="اشتراک‌گذاری در گفتگو"
          subtitle="یک مورد از پنل انتخاب کن تا در پیام فرستاده بشه"
        />
      }
    >
      <Segmented
        block
        value={type}
        onChange={setType}
        options={options}
        className="mb-3"
      />

      <Input.Search
        allowClear
        autoFocus
        placeholder="حداقل دو حرف بنویس…"
        value={searchInput}
        onChange={(event) => {
          setSearchInput(event.target.value);
          setSearch(event.target.value);
        }}
      />

      <Spin spinning={isFetching}>
        <div className={styles.results}>
          {!enabled ? (
            <p className={styles.hint}>
              برای پیدا کردن مورد، بخشی از عنوانش رو بنویس.
            </p>
          ) : data?.length ? (
            data.map((entity) => (
              <button
                key={`${entity.type}-${entity.id}`}
                type="button"
                className={styles.result}
                onClick={() => {
                  onPick(entity);
                  reset();
                  onClose();
                }}
              >
                <ChatEntityCard entity={entity} compact asLink={false} />
              </button>
            ))
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="چیزی پیدا نشد"
            />
          )}
        </div>
      </Spin>
    </Modal>
  );
};

export default ChatEntityPicker;
